import { Link } from 'react-router-dom';
import { ArrowRight, Check, MapPin, UtensilsCrossed, BookOpen, Camera, Search } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import BackToTop from '@/components/BackToTop';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import { usePricing } from '@/context/PricingContext';

const FEATURES = [
  { icon: BookOpen, title: 'Online Menus', desc: 'Beautiful, mobile-friendly menus your customers love browsing' },
  { icon: MapPin, title: 'Reservation System', desc: 'Let diners book a table directly from your website' },
  { icon: Camera, title: 'Food Photography', desc: 'Showcase your dishes with a mouthwatering gallery' },
  { icon: Search, title: 'Local SEO', desc: 'Rank for "restaurant Bournemouth" and get found on Google Maps' },
];

const RESULTS = [
  'Get more reservations and walk-ins',
  'Show up when locals search "restaurants near me"',
  'Display your menu beautifully on any device',
  'Build trust with reviews and food photography',
  'Compete with chains using a fraction of their budget',
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "UI Designs — Web Design for Restaurants in Bournemouth",
  "description": "Professional websites for restaurants and cafes in Bournemouth. Online menus, reservations, and local SEO to get more covers.",
  "url": "https://uidesigns.co.uk/web-design-restaurants-bournemouth",
  "areaServed": { "@type": "City", "name": "Bournemouth" },
  "serviceType": ["Web Design for Restaurants", "Cafe Websites", "Local SEO"]
};

export default function WebDesignRestaurants() {
  const pricing = usePricing();
  const llPrice = pricing.local_launch?.price || "399";

  const scrollToContact = () => { window.location.href = '/contact'; };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead
        title="Web Design for Restaurants Bournemouth | Cafe & Restaurant Websites | UI Designs"
        description="Get more covers with a professional restaurant website in Bournemouth. Online menus, reservations, and Google Maps visibility. From £800."
        canonical="https://uidesigns.co.uk/web-design-restaurants-bournemouth"
        keywords="restaurant website Bournemouth, cheap web design restaurants, cafe website design, online menu website, affordable website Poole Christchurch"
        schema={SCHEMA}
      />
      <Navigation />

      <main>
        {/* Hero */}
        <section data-testid="restaurants-hero" className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#F8F9FA] to-white dark:from-[#0A0A0A] dark:to-[#0A0A0A] pointer-events-none" />
          <div className="absolute top-10 right-[10%] w-[500px] h-[500px] bg-[#00E5FF]/8 rounded-full blur-[140px] pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <ScrollReveal>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-8">
                  <UtensilsCrossed className="w-3 h-3 text-[#00E5FF]" />
                  Restaurant & Cafe Websites
                </span>
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.08] text-[#0A0A0A] dark:text-white mb-6">
                  Web Design for Restaurants
                  <br />
                  <span className="text-gradient-shimmer">in Bournemouth</span>
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <p className="text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400 mb-8 max-w-xl">
                  Your Bournemouth restaurant needs a website that makes people hungry. I build beautiful sites with online menus, table reservations, and local SEO — perfect for cafes near Bournemouth Beach, bistros in Westbourne, or takeaways in Boscombe.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={300}>
                <button onClick={scrollToContact} data-testid="restaurants-cta" className="group inline-flex items-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-8 py-4 rounded-full font-medium transition-all hover:bg-[#00E5FF] hover:text-black dark:hover:bg-violet-400 pulse-glow hover:scale-[1.02]">
                  Get Your Free Quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Features */}
        <section data-testid="restaurants-features" className="py-20 md:py-28 bg-[#F8F9FA] dark:bg-[#0D0D0D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-12">
                What Your Restaurant Website <span className="text-[#00E5FF]">Includes</span>
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {FEATURES.map((f, i) => (
                <ScrollReveal key={f.title} delay={i * 100}>
                  <div className="bg-white dark:bg-[#111111] border border-slate-200 dark:border-white/10 rounded-2xl p-6">
                    <f.icon className="w-5 h-5 text-[#00E5FF] mb-4" />
                    <h3 className="font-['Outfit'] text-base font-semibold text-[#0A0A0A] dark:text-white mb-1">{f.title}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{f.desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Results */}
        <section data-testid="restaurants-results" className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <ScrollReveal>
                  <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-6">
                    More Covers. More Takeaways. More Regulars.
                  </h2>
                  <p className="text-base text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                    When someone near Bournemouth Town Centre or Southbourne is looking for somewhere to eat, your website needs to make them choose you. A fast, beautiful site with a clear menu and easy booking does exactly that.
                  </p>
                </ScrollReveal>
                <ScrollReveal delay={100}>
                  <ul className="space-y-3">
                    {RESULTS.map((r, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#00E5FF]" strokeWidth={3} />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{r}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollReveal>
              </div>
              <ScrollReveal delay={200}>
                <div className="bg-[#0A0A0A] rounded-2xl p-8 md:p-10 text-center">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Restaurant websites from</p>
                  <p className="font-['Outfit'] text-5xl font-black text-white mb-2">£{llPrice}</p>
                  <p className="text-sm text-slate-400 mb-6">Get online fast — upgrade anytime</p>
                  <button onClick={scrollToContact} className="w-full inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-white transition-all">
                    Get Started <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-16 bg-[#F8F9FA] dark:bg-[#0D0D0D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-6">More local web design</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/web-design-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Web Design Bournemouth</Link>
                <Link to="/web-design-barbers-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Web Design for Barbers</Link>
                <Link to="/web-design-gyms-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Web Design for Gyms</Link>
                <Link to="/web-design-trades-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Trades & Construction</Link>
                <Link to="/blog" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Blog</Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <ScrollReveal>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-4">
                Ready to Fill More Tables?
              </h2>
              <p className="text-base text-slate-600 dark:text-slate-400 mb-8">Free quote, no obligation. Let's get your restaurant online properly.</p>
              <button onClick={scrollToContact} className="group inline-flex items-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-8 py-4 rounded-full font-medium transition-all hover:bg-[#00E5FF] hover:text-black dark:hover:bg-violet-400 pulse-glow">
                Get Your Free Quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <Footer />
      <CookieConsent />
      <BackToTop />
    </div>
  );
}
